import { Alert, Button, Card, CardContent, Collapse, Container, Divider, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, Switch, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../Common/_hooks/useAppSelector";
import { ChangeEvent, FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import SelectV2 from "../../Common/Form/SelectV2";
import SelectAddressDialog from "../Settings/Addresses/SelectAddressDialog";
import SelectCreditCardDialog from "../Settings/CreditCards/SelectCreditCardDialog";
import { FetchOneItem } from "../../UserData/_actions/UserDataActions";
import { useAppDispatch } from "../../Common/_hooks/useAppDispatch";
import CreditCardIcon from "../Settings/CreditCards/CreditCardIcon";
import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router";
import useCheckStoreFeatures from "../../Common/_hooks/Store/useCheckStoreFeatures";
import { DeleteStore, SaveStoreSettings, StoreActionType } from "../../UserData/_actions/Store/StoreActions";
import AuthCheckDialog from "../Settings/AuthCheckDialog";
import StoreHeader from "./StoreHeader";
import useStoreData from "../../Common/_hooks/Store/useStoreData";

export default function StoreSettings() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [t] = useTranslation('account-v2')
  const [tR] = useTranslation('routes')

  const params = useParams<{ storeSlug: string }>()
  const { canAutocancel, canAutoship } = useCheckStoreFeatures(params.storeSlug ?? '')

  const loader = useAppSelector(state => state.get('UIData').get('loaders').get('userData'))
  const countries = useAppSelector(state => state.get('appData').get('countries'))
  const store = useStoreData(params.storeSlug)

  const [autopay, setAutopay] = useState(false);
  const [autocancel, setAutocancel] = useState(false);
  const [autoship, setAutoship] = useState(false);
  const [importUnsynced, setImportUnsynced] = useState(false);
  const [billingAddressId, setBillingAddressId] = useState<number | null>(null);
  const [paymentMethodId, setPaymentMethodId] = useState<string>('');
  const [addressDialogOpened, setAddressDialogOpened] = useState(false)
  const [creditCardDialogOpened, setCreditCardDialogOpened] = useState(false)
  const [passCheckDialogOpened, setPassCheckDialogOpened] = useState(false)

  const billingAddress = useAppSelector(state => state.get('userData').get('addresses').get('data').get(String(billingAddressId)))
  const creditCard = useAppSelector(state => paymentMethodId ? state.get('userData').get('creditCards').get('data').get(paymentMethodId.split('-')[1]) : undefined)

  const onChangeAutopay = useCallback((e: ChangeEvent<HTMLInputElement>) => setAutopay(e.target.value === "1"), [])
  const onChangeAutocancel = useCallback((e: ChangeEvent<HTMLInputElement>) => setAutocancel(e.target.value === "1"), [])
  const onChangeAutoship = useCallback((e: ChangeEvent<HTMLInputElement>) => setAutoship(e.target.value === "1"), [])
  const onChangeImportUnsynced = useCallback((e: ChangeEvent<HTMLInputElement>, checked: boolean) => setImportUnsynced(checked), [])

  const onAddressDialogOpen = useCallback(() => setAddressDialogOpened(true), [])
  const onAddressDialogClose = useCallback(() => setAddressDialogOpened(false), [])

  const onSelectAddress = useCallback((id: number) => {
    setBillingAddressId(id)
    onAddressDialogClose()
  }, [onAddressDialogClose])

  const onCreditCardDialogOpen = useCallback(() => setCreditCardDialogOpened(true), [])
  const onCreditCardDialogClose = useCallback(() => setCreditCardDialogOpened(false), [])

  const onSelectCreditCard = useCallback((id: number) => {
    setPaymentMethodId('cc-' + String(id))
    onCreditCardDialogClose()
  }, [onCreditCardDialogClose])

  const onPassCheckDialogClose = useCallback(() => {
    setPassCheckDialogOpened(false)
  }, [])

  const onAuthCheck = useCallback((password: string) => {
    if (!store) return;

    dispatch(DeleteStore(
      store.get('slug'),
      password
    ).set({
      onSuccess: () => {
        setPassCheckDialogOpened(false)
        navigate(tR('/account/stores'))
      }
    }))
  }, [store, tR])

  const onSubmitSettings = useCallback((e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!store) return;

    dispatch(SaveStoreSettings(
      store.get('slug'),
      autopay,
      paymentMethodId,
      billingAddressId,
      autocancel,
      autoship,
      importUnsynced,
    ))
  }, [store, autopay, billingAddressId, paymentMethodId, autocancel, autoship, importUnsynced])

  const onDeleteStore = useCallback(() => setPassCheckDialogOpened(true), [])

  useEffect(() => {
    if (!store) return;

    setAutopay(store.get('autopay'))
    setBillingAddressId(store.get('billing_address'))
    setPaymentMethodId(store.get('payment_method'))
    setAutocancel(store.get('autocancel'))
    setAutoship(store.get('autoship'))
    setImportUnsynced(store.get('import_unsynced'))
  }, [store])

  useEffect(() => {
    if (billingAddressId === null) return;

    dispatch(FetchOneItem(String(billingAddressId), 'addresses'))
  }, [billingAddressId])

  useEffect(() => {
    const creditCardId = paymentMethodId.split('-')[1];
    if (!creditCardId) return;

    dispatch(FetchOneItem(creditCardId, 'creditCards'))
  }, [paymentMethodId])

  const addressLine = useMemo(() => {
    if (!billingAddress) return null;

    const countryObj = countries.get(String(billingAddress.get('country')))

    return billingAddress.get('address') +
      (billingAddress.get('address2') !== '' ? ', ' + billingAddress.get('address2') : '') +
      (billingAddress.get('city') !== '' ? ', ' + billingAddress.get('city') : '') +
      (billingAddress.get('postal_code') !== '' ? ', ' + billingAddress.get('postal_code') : '') +
      (billingAddress.get('province') !== '' ? ', ' + billingAddress.get('province') : '') +
      (countryObj ? ', ' + countryObj.get('name') : '')
  }, [billingAddress, countries])

  const creditCardExpiration = useMemo(() => {
    if (!creditCard) return null;
    return DateTime.fromISO(creditCard.get('expiration')).endOf('month')
  }, [creditCard])

  if (!store || !store.get('details_loaded')) return null;

  return <div>
    <StoreHeader
      title={t('Settings')}
    />

    <Container
      maxWidth="sm"
      disableGutters
      style={{ margin: 0 }}
    >
      <Stack spacing={4}>
        <Card elevation={0}>
          <form onSubmit={onSubmitSettings}>
            <CardContent sx={{ py: 2 }}>
              <Stack spacing={1}>
                <Typography
                  variant="h3"
                >{t('Preferences')}</Typography>
              </Stack>
            </CardContent>
            <Divider />
            <CardContent>
              <Stack spacing={4}>
                <FormControl
                  disabled={loader}
                >
                  <FormLabel>{t('Order Import')}</FormLabel>
                  <RadioGroup
                    value={autopay ? "1" : "0"}
                    onChange={onChangeAutopay}
                    row
                  >
                    <FormControlLabel
                      value="0"
                      control={<Radio />}
                      label={<Stack spacing={0.5}>
                        <Typography variant="body2">{t('Manually confirm orders')}</Typography>
                        <Typography variant="caption">{t('We will email you each time you make a sale on your store. You will need to login and pay for the order before it begins processing.')}</Typography>
                      </Stack>}
                    />
                    <FormControlLabel
                      value="1"
                      control={<Radio />}
                      label={<Stack spacing={0.5}>
                        <Typography variant="body2">{t('Automatically confirm orders')}</Typography>
                        <Typography variant="caption">{t('Automatically charges your credit card as orders are paid for in your store and the orders start processing immediately.')}</Typography>
                        <Collapse in={autopay}>
                          <Stack spacing={2}>
                            <Stack spacing={1}>
                              <Typography variant="h4">{t('Billing address')}</Typography>

                              <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
                                {addressLine}

                                <Button
                                  color="secondary"
                                  variant="outlined"
                                  onClick={onAddressDialogOpen}
                                  style={{
                                    flexShrink: 0
                                  }}
                                >{t('Select Address')}</Button>
                              </Stack>
                            </Stack>

                            <Stack spacing={1}>
                              <Typography variant="h4">{t('Payment method')}</Typography>

                              <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
                                {creditCard ? <div>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <CreditCardIcon brand={creditCard.get('brand')} />
                                    <Typography variant="body1">&bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; &bull;&bull;&bull;&bull; {creditCard.get('last4')}</Typography>
                                  </Stack>
                                  {creditCardExpiration ? <>
                                    <Typography variant="caption">{t('Expiration: {{date}}', { date: creditCardExpiration.toLocaleString({ month: '2-digit', year: '2-digit' }) })}</Typography>
                                    {creditCardExpiration.diffNow().as('months') < 0
                                      ? <Typography variant="subtitle1" color="error.main">{t('Expired')}</Typography>
                                      : (creditCardExpiration.diffNow().as('months') < 2
                                        ? <Typography variant="subtitle1" color="warning.main">{t('Expires soon')}</Typography>
                                        : null)}
                                  </> : null}
                                </div> : null}

                                <Button
                                  color="secondary"
                                  variant="outlined"
                                  onClick={onCreditCardDialogOpen}
                                >{t('Select Credit card')}</Button>
                              </Stack>
                            </Stack>
                          </Stack>
                        </Collapse>
                      </Stack>}
                    />
                  </RadioGroup>
                </FormControl>

                {canAutocancel ? <Stack spacing={2}>
                  <FormControl
                    disabled={loader}
                  >
                    <FormLabel>{t('Order Cancellation')}</FormLabel>
                    <RadioGroup
                      value={autocancel ? "1" : "0"}
                      onChange={onChangeAutocancel}
                      row
                    >
                      <FormControlLabel
                        value="0"
                        control={<Radio />}
                        label={<Stack spacing={0.5}>
                          <Typography variant="body2">{t('Manually cancel orders')}</Typography>
                          <Typography variant="caption">{t('Whenever an order gets cancelled on your store, you will have to login and manually cancel the corresponding order yourself in order to be refunded.')}</Typography>
                        </Stack>}
                      />
                      <FormControlLabel
                        value="1"
                        control={<Radio />}
                        label={<Stack spacing={0.5}>
                          <Typography variant="body2">{t('Automatically cancel orders')}</Typography>
                          <Typography variant="caption">{t('Automatically cancel the corresponding order and get refunded whenever one of your order on your store gets cancelled.')}</Typography>
                        </Stack>}
                      />
                    </RadioGroup>
                  </FormControl>
                  <Alert severity="info">{t('You have a grace period of 6 hours to cancel your order. We cannot cancel your order after the grace period.')}</Alert>
                </Stack> : null}

                {canAutoship ? <Stack spacing={2}>
                  <FormControl
                    disabled={loader}
                  >
                    <FormLabel>{t('Order Shipping')}</FormLabel>
                    <RadioGroup
                      value={autoship ? "1" : "0"}
                      onChange={onChangeAutoship}
                      row
                    >
                      <FormControlLabel
                        value="0"
                        control={<Radio />}
                        label={<Stack spacing={0.5}>
                          <Typography variant="body2">{t('Manually mark orders as shipped')}</Typography>
                          <Typography variant="caption">{t('When we ship your order, you will receive a shipping notification from us, but you will have to go on your store to manually mark the order as shipped.')}</Typography>
                        </Stack>}
                      />
                      <FormControlLabel
                        value="1"
                        control={<Radio />}
                        label={<Stack spacing={0.5}>
                          <Typography variant="body2">{t('Automatically mark orders as shipped')}</Typography>
                          <Typography variant="caption">{t('When we ship your order, we will automatically mark it as shipped on your store. If you also sell items that are not from us, we recommend keeping this setting off so you can manually mark your orders as shipped when the other items are shipped as well.')}</Typography>
                        </Stack>}
                      />
                    </RadioGroup>
                  </FormControl>
                </Stack> : null}

                <FormControlLabel
                  control={<Switch
                    color="primary"
                    checked={importUnsynced}
                    onChange={onChangeImportUnsynced}
                  />}
                  label={<Stack spacing={0.5}>
                    <Typography variant="body2">{t('Import orders with unsynced items')}</Typography>
                    <Typography variant="caption">{t('Items that are not synced will still be imported when they are ordered, and you can sync them as they come in. If you already have a store with products, this is the best way to get started.')}</Typography>
                  </Stack>}
                  //labelPlacement="start"
                  /*componentsProps={{
                    typography: {
                      variant: 'h3'
                    }
                  }}*/
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginLeft: 0
                  }}
                />

                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loader}
                  style={{
                    alignSelf: 'flex-start'
                  }}
                >{t('Save settings')}</Button>
              </Stack>
            </CardContent>
          </form>
        </Card>

        <Card elevation={0}>
          <CardContent sx={{ py: 2 }}>
            <Stack spacing={1}>
              <Typography
                variant="h3"
              >{t('Remove {{store}} integration', { store: store.get('name') })}</Typography>
              <Typography
                variant="body1"
              >{t('Removing your {{store}} integration will remove all product and order syncing between your store and Art of Where. No products will be removed from your {{store}} store, nor from Art of Where.', { store: store.get('name') })}</Typography>
            </Stack>
          </CardContent>
          <Divider />
          <CardContent>
            <Button
              variant="outlined"
              color="error"
              onClick={onDeleteStore}
            >{t('Remove {{store}} integration', { store: store.get('name') })}</Button>
          </CardContent>
        </Card>
      </Stack>
    </Container>

    <SelectAddressDialog
      opened={addressDialogOpened}
      onClose={onAddressDialogClose}
      onSelect={onSelectAddress}
    />

    <SelectCreditCardDialog
      opened={creditCardDialogOpened}
      onClose={onCreditCardDialogClose}
      onSelect={onSelectCreditCard}
    />

    <AuthCheckDialog
      opened={passCheckDialogOpened}
      onConfirm={onAuthCheck}
      onClose={onPassCheckDialogClose}
      loaderKey="edit_store"
      errorKey={StoreActionType.DeleteStore}
    />
  </div>
}
